/**
 * usePricingJsonLd — a pricing page's Product nodes from the catalog RESOURCE it renders (APP-ENTITLEMENT-001
 * Phase 11): nothing before the catalog is read, then `pricingJsonLd` over the same data the section shows, worded
 * from the two bundles every marketing site registers — the catalog's unit words (`accountAppsUi`) and this
 * package's own sentence for a product. Nine pages, one builder, no unit word typed in an app.
 *
 * @module @puffin-core/marketing-shell/seo/usePricingJsonLd
 */

import { useMemo } from 'react';
import { unitKeyOf, useAccountAppsUiTranslate } from '@puffin-core/account-apps-ui/copy';

import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';
import { pricingJsonLd } from './pricingJsonLd.js';

/**
 * @param {Object} params
 * @param {{status: string, data?: Object}} params.catalog - the public catalog as a resource (`useAppCatalog`).
 * @param {string} params.pageUrl - the pricing page's canonical URL.
 * @param {Function} [params.t] - the host's translator, honoured first.
 * @returns {Object[]} Product nodes; empty until the catalog is ready.
 */
export function usePricingJsonLd({ catalog, pageUrl, t: hostT }) {
  const t = useMarketingShellTranslate(hostT);
  const tApps = useAccountAppsUiTranslate(hostT);
  const data = catalog.status === 'ready' ? catalog.data : null;
  return useMemo(
    () =>
      data === null
        ? []
        : pricingJsonLd({
            catalog: data,
            pageUrl,
            unitTextOf: (dimension) =>
              t('pricing.jsonLdUnitText', { unit: tApps(unitKeyOf(dimension.dimensionKey)) }),
            describe: (entry) => t('pricing.jsonLdProductDescription', { app: entry.label }),
          }),
    [data, pageUrl, t, tApps],
  );
}

export default usePricingJsonLd;
